import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BuyerSubmissionDetailDto } from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import { Button, Input, useToast } from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { calendarDateInputToIso, isoDateInTz, useUserTimeZone } from "@/utils/datetime";

/**
 * Record — or correct — the day the seller's financials arrived.
 *
 * <p>🔴 <b>The business event was specified and then left unreachable.</b>
 * <c>StampMilestoneFor</c> stamps <c>CimReceivedAtUtc ??= now</c> when the
 * status moves to "Info received" and deliberately stamps nothing else, with a
 * comment saying which of the two documents arrived "is recorded by the
 * specialist on the detail form" and that stamping both "would claim documents
 * nobody has seen". No such form existed, so <c>FinancialsReceivedAtUtc</c> had
 * no writer anywhere in the system: read by the detail DTO and by the milestone
 * ladder, written by nothing, so rung 6 of 10 could not be reached by any path
 * through the product. This is that form.</p>
 *
 * <p><b>A MODAL rather than an always-open form, since WS7.</b> It used to sit
 * permanently expanded inside the Diligence card — a label, a date input, a
 * button and two paragraphs of caveat, occupying roughly half the card's height
 * on every read of the tab, to serve a case that arises once per submission at
 * most and usually never. The two ways in are now the two things a specialist
 * is actually doing: the checklist row's own door when nothing is recorded yet,
 * and the section's edit pencil when a recorded date needs correcting.</p>
 *
 * <p><b>Editable rather than a one-way "mark received" button, deliberately.</b>
 * On this endpoint an explicit value WINS — the shape <c>RecordNdaAsync</c>
 * already chose for <c>NdaSignedAtUtc</c>, and for the same reason: a date typed
 * by a human can be typed wrong, and under <c>??=</c> the first mis-keyed year
 * would be permanent with no way back. Forward-only is preserved by there being
 * no clear flag at all — null means unchanged, so nothing this endpoint accepts
 * can put the stamp back to null and counting can never lose credit already
 * earned.</p>
 *
 * <p>The date defaults to the stamp when there is one and to today when there is
 * not, because those are the two things a specialist opening this is doing:
 * fixing a date they typed wrong, or recording a document that has just landed.
 * A future date is refused — a milestone records something that has already
 * happened.</p>
 */
export function RecordFinancialsReceivedModal({
  s,
  open,
  onClose,
}: {
  s: BuyerSubmissionDetailDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const tz = useUserTimeZone();

  const stamped = s.financialsReceivedAtUtc?.slice(0, 10) ?? null;
  const [date, setDate] = useState(() => stamped ?? today(tz));

  // Re-seed when the record changes underneath — a save returns the new stamp,
  // and leaving the box on the pre-save value would show one date beside a field
  // reading another.
  const [seededFrom, setSeededFrom] = useState(stamped);
  if (stamped !== seededFrom) {
    setSeededFrom(stamped);
    setDate(stamped ?? today(tz));
  }

  const record = useMutation({
    mutationFn: (receivedAt: string) =>
      buyerSubmissionsApi.update(s.id, { financialsReceivedAtUtc: receivedAt }),
    onSuccess: () => {
      // The whole prefix: the detail read renders this stamp in two places — the
      // checklist row and the milestone ladder on Overview's rail — and the
      // submission list derives from the same record.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success(stamped ? "Date corrected." : "Financials recorded.");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const future = date > today(tz);
  const unchanged = date === stamped;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={stamped ? "Correct the date the financials arrived" : "Record the seller financials"}
    >
      <div className="grid gap-space-4" data-testid="financials-received-control">
        <p className="text-small text-ink m-0">
          {stamped
            ? "The date can be corrected but not removed — a milestone that has been reached stays reached."
            : "Records the day the seller's P&L and balance sheet reached Phoenix. The documents themselves belong in this submission's Documents tab."}
        </p>

        <div className="grid gap-space-2">
          <label htmlFor="financials-received-at" className="block text-caption text-gray-mid">
            {stamped ? "Correct the date" : "Date the financials arrived"}
          </label>
          <Input
            id="financials-received-at"
            data-testid="financials-received-date"
            type="date"
            className="w-auto"
            value={date}
            max={today(tz)}
            onChange={(e) => setDate(e.target.value)}
          />
          {future && (
            <p className="text-caption text-status-warning-ink m-0">
              A milestone records something that has already happened, so this
              cannot be a future date.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="financials-received-submit"
            disabled={!date || future || unchanged || record.isPending}
            onClick={() => record.mutate(calendarDateInputToIso(date)!)}
          >
            {record.isPending
              ? "Saving…"
              : stamped
                ? "Save the corrected date"
                : "Record financials received"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Today as `YYYY-MM-DD` in the VIEWER'S zone, which is what a `type="date"`
 * input wants.
 *
 * <p>Read through `isoDateInTz` rather than shifting an instant by the
 * BROWSER's offset: that spelling produces a third "today" agreeing with
 * neither UTC nor the app's zone, and it disagrees with every other date this
 * product renders. The seed, the `max=` bound and the submit gate all call
 * this one function, so they cannot drift apart.</p>
 */
function today(tz: string): string {
  return isoDateInTz(new Date(), tz);
}
